import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg } from "@/lib/residentApi";
import { inputCls, GoldButton } from "./ui";
import { MemoAssist } from "@/components/memo/MemoAssist";

const PH = /\[STAFF:\s*confirm/i;
export const letter = (info) => `Hi ${info?.first_name || ""},\n\n\n\nWarmly,\n${[info?.signature?.name, info?.signature?.title].filter(Boolean).join("\n")}`;

/** Auto-growing letter-style staff reply with an optional AI draft. Nothing sends until staff press the button. */
export default function MemoReply({ m, info, onSent, onCancel }) {
  const [text, setText] = useState(() => letter(info));
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [needs, setNeeds] = useState(false);
  const [ask, setAsk] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + 2}px`; } }, [text]);
  const blank = text.trim() === letter(info).trim() || !text.trim();
  const ph = PH.test(text);
  const draft = async () => {
    setAsk(false); setDrafting(true); setNeeds(false);
    try {
      const d = await reApi.post(`/memos/${m.id}/draft`, {});
      if (d.needs_person) { setNeeds(true); setText(""); setAi(false); }
      else { setText(d.draft); setAi(true); }
    } catch (e) { toast.error(errMsg(e)); } finally { setDrafting(false); }
  };
  const send = async () => {
    setBusy(true);
    try { await reApi.post(`/memos/${m.id}/replies`, { body: text, ai_assisted: ai }); toast.success("Reply sent"); onSent(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 pt-1" data-testid={`memo-reply-box-${m.id}`}>
      {info?.can_draft && <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={drafting} onClick={() => (blank ? draft() : setAsk(true))} data-testid={`memo-ai-draft-${m.id}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-nex-gold/40 font-mono text-[10px] uppercase tracking-[0.14em] text-nex-gold hover:bg-nex-gold/10 disabled:opacity-40 transition-colors">
          <Sparkles className="h-3 w-3" /> {drafting ? "Drafting…" : "Draft reply"}</button>
        {ask && <span className="text-[11px] text-nex-muted" data-testid={`memo-ai-replace-ask-${m.id}`}>Replace what's in the box?
          <button type="button" onClick={draft} data-testid={`memo-ai-replace-yes-${m.id}`} className="ml-2 text-nex-gold">Replace</button>
          <button type="button" onClick={() => setAsk(false)} data-testid={`memo-ai-replace-no-${m.id}`} className="ml-2 text-nex-muted">Keep mine</button></span>}
        {ai && <span className="text-[11px] text-amber-300" data-testid={`memo-ai-label-${m.id}`}>AI draft — review before sending.</span>}
      </div>}
      {needs && <div className="flex items-center gap-1.5 text-xs text-red-300 p-2 rounded-sm border border-red-400/30 bg-red-500/10" data-testid={`memo-needs-person-${m.id}`}>
        <AlertOctagon className="h-3.5 w-3.5" /> Needs a person now — this memo mentions an emergency or safety concern. Reach the resident directly.</div>}
      <textarea ref={ref} rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid={`memo-reply-input-${m.id}`} className={`${inputCls} resize-none overflow-hidden leading-relaxed min-h-[180px]`} />
      <MemoAssist value={text} onChange={setText} id={`memo-reply-${m.id}`} />
      {ph && <p className="text-[11px] text-red-300" data-testid={`memo-reply-placeholder-${m.id}`}>Fill in every [STAFF: confirm…] placeholder before sending.</p>}
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} data-testid={`memo-reply-cancel-${m.id}`} className="font-mono text-[10px] uppercase text-nex-muted hover:text-nex-silver">Cancel</button>
        <GoldButton onClick={send} disabled={busy || blank || ph} data-testid={`memo-reply-send-${m.id}`} className="ml-auto"><Send className="h-3 w-3" /> {ai ? "Approve and Send" : "Send reply"}</GoldButton>
      </div>
    </div>
  );
}
